import {computed, reactive, ref} from "vue";

const usePost = () => {

  // 投稿を保存する localStorage のキー
  const localStorageKey = 'placePostData';

  //この composition が扱うデータ
  const state = reactive({
    //キャンペーンタグ ID
    campaignTagId: 0,

    //キャンペーンカテゴリ ID
    campaignCategoryId: 0,

    // キャンペーン投稿が既に 3 回投稿されている
    campaignLimit: false,

    //エラー
    errors: {},

    //感情(0: 未選択, 1: キャンペーン)
    emotion: 0,

    //カテゴリ ID
    categoryIds: [],

    //ニックネーム(キー: ID, 値: 名前文字列)
    nicknames: [],

    //投稿カテゴリー(キー: ID, 値: 名前文字列)
    categories: [],

    //ニックネーム ID (0: ユーザーネーム, -1 匿名)
    nicknameId: -2,

    //件名
    title: '',

    //本文
    content: '',

    //下書きから復帰した場合のみセットされる下書き id
    draftId: 0,

    //コメント許可
    acceptComments: true,

    // 回答希望
    isAnswersWanted: false,
  });

  //選択されているニックネームを文字列で返す
  const nickname = computed(() => {
    //選択されている id から適合する name を検索する
    const name = state.nicknames.find(row => Number(row.id) === Number(state.nicknameId));

    //存在すれば name プロパティを返す
    return name ? name.name : '未選択';
  });

  //エラー描写用のオブジェクトを返す
  const errorLines = computed(() => {
    //返す値
    const lines = {};

    //errors があれば加える
    if ('errors' in state.errors) {
      //キー配列の用意
      const keys =  Object.keys(state.errors.errors);

      //追加
      for (let i = 0, max = keys.length; i < max; i = (i + 1) | 0) {
        lines[keys[i]] = state.errors.errors[keys[i]].join('、');
      }
    }

    //lines がこの時点で空であれば messages を挿入
    if (Object.keys(lines).length === 0 && 'message' in state.errors) {
      lines.message = state.errors.message;
    }

    //返す
    return lines;
  });

  //本文の文字数を正確に数える
  // https://qiita.com/ka215/items/d059a78e29adef3978b5#6-%E3%83%9E%E3%83%AB%E3%83%81%E3%83%90%E3%82%A4%E3%83%88%E6%96%87%E5%AD%97%E3%81%AB%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%9F%E6%96%87%E5%AD%97%E5%88%97%E5%87%A6%E7%90%86
  const contentLength = computed(() => {

    //指定された範囲にサロゲートペアが存在するかを boolean で返す
    //upper は 1 バイト目、lower に 2 バイト目を指定する
    //UTF の場合、通常は 2 バイトで一文字だが、サロゲートペアの場合は 4 バイトで一文字を表す
    const isSurrogatePair = (upper, lower) => 0xD800 <= upper && upper <= 0xDBFF && 0xDC00 <= lower && lower <= 0xDFFF;

    //最終的に返す文字数
    let result = 0;

    //サロゲートペアがあればそれを 2 文字ではなく 1 文字としてカウントしつつ文字数を返す
    for (let i = 0; i < state.content.length; i++,result++) {
      //サロゲートペアでない文字は一文字とカウントする
      if ( ! isSurrogatePair(state.content.charCodeAt(i), state.content.length > (i + 1) ? state.content.charCodeAt(i + 1) : 0)) {
        continue;
      }

      //サロゲートペアなら一文字ぶん多くカウントする
      i++;
    }

    //スペースと改行を文字として除外する
    for (let i = 0; i < state.content.length; i++) {
      //スペース or 改行でなければ何もしない
      if ( ! /\r\n|\r|\n| |　/.test(state.content.charAt(i))) {
        continue;
      }

      //文字カウントを減らす
      result--;
    }

    //結果文字列の数を返す
    return result;
  });

  //本文が文字数制限内におさまっているかを返す
  const isContentLength = computed(() => contentLength.value >= 1 && contentLength.value <= 1000);

  //感情選択、ニックネーム選択、タイトル、本文が正しく設定されているかを返す
  const isEditComplete = computed(() => {
    //本文文字数が異常であれば false を返す
    if ( ! isContentLength.value) {
      return false;
    }

    //件名が空白であれば false を返す
    if (state.title === '') {
      return false;
    }

    //ニックネームが選択されていなければ false, 全て問題なければ true を返す
    return Number(state.nicknameId) !== -2;
  });

  // localStorage にデータを保存し続ける
  const _memoryLocalStorage = () => {
    // localStorage に保存
    window.localStorage.setItem(localStorageKey, JSON.stringify({
      emotion: state.emotion,
      categoryIds: state.categoryIds,
      nicknameId: state.nicknameId,
      title: state.title,
      content: state.content,
      acceptComments: state.acceptComments,
      isAnswersWanted: state.isAnswersWanted,
      loginUserId: loginUserId
    }));

    // 0.5 秒後に再起動
    setTimeout(_memoryLocalStorage, 500);
  };

  // localStorage に保存されているデータを読み出す
  const _restoreLocalStorage = () => {
    // localStorage に保存されているデータを取得
    const data = window.localStorage.getItem(localStorageKey);

    // localStorage にデータが存在しなければ何もしない
    if ( ! data) {
      return;
    }

    // localStorage に保存されているデータをオブジェクトに変換
    const json = JSON.parse(data);

    // loginUserId が現在のユーザ ID と異なる場合は何もしない
    if (Number(json.loginUserId) !== Number(loginUserId)) {
      return;
    }

    // localStorage に保存されているデータを state に反映
    state.emotion = json.emotion;
    state.categoryIds = json.categoryIds;
    state.nicknameId = json.nicknameId;
    state.title = json.title;
    state.content = json.content;
    state.acceptComments = json.acceptComments;
    state.isAnswersWanted = json.isAnswersWanted;
  };

  /**
   * POST 送信時に必須の HTTP ヘッダー設定を返す
   * @returns {{"X-Requested-With": string, "X-CSRF-TOKEN"}}
   * @private
   */
  const _getHeader = () => {
    return {
      'X-Requested-With': 'XMLHttpRequest',
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
    };
  };

  /**
   * 現状の入力値を得る
   * @returns {FormData|null}
   * @private
   */
  const _getFormData = () => {
    //フォームデータを挿入する
    const formData =  new FormData();

    //値を追加する
    formData.append('nickname_id', state.nicknameId);
    formData.append('emotion', state.emotion);
    formData.append('title', state.title);
    formData.append('content', state.content.replace(/\r\n/g, "\n"));
    formData.append('draft_id', state.draftId);
    for (let i = 0, max = state.categoryIds.length; i < max; i = (i + 1) | 0) {
      formData.append('post_category_ids[]', state.categoryIds[i]);
    }
    if (state.acceptComments) {
      formData.append('accept_comments', '1');
    }
    if (state.isAnswersWanted) {
      formData.append('is_answers_wanted', '1');
    }
    if (state.campaignTagId) {
      formData.append('campaign_tag_id', state.campaignTagId);
    }
    if (state.campaignCategoryId) {
      formData.append('campaign_category_id', state.campaignCategoryId);
    }

    //返す
    return formData;
  };

  /**
   * 初期情報をサーバーより得て反映する
   * @return {Promise<void>}
   */
  const init = async () => {
    // localStorage に保存されているデータを読み出す
    _restoreLocalStorage();

    // サーバーに保存されているデータを読み出す
    try {
      //サーバーより情報を得る
      const response = await fetch(siteUrl+'/place/init?campaignTagId='+state.campaignTagId);

      //レスポンス異常発生
      if ( ! response.ok) {
        //もし 404 だったらログインしていない
        if (response.status === 404) {
          throw new Error('ログインセッションが切れている可能性があります。恐れ入りますがページをリロードして再度ログインしてください。');
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //パラメータを得る
      const parameter = await response.json();

      //情報を更新する
      state.nicknames = parameter.nicknames;
      state.categories = parameter.categories;
      state.campaignLimit = parameter.campaignLimit;

      // ローカルストレージからの読み出しを開始する
      _memoryLocalStorage();
    }

      //サーバーとの通信中、なんらかのエラーが発生
    catch (error) {
      //恐らく通信途絶
      if (error instanceof TypeError) {
        state.errors = {messages: 'ネットワーク接続に失敗しました。'};
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        state.errors = {messages: 'ネットワーク接続が中断されました。'};
      }

      //恐らくカスタムエラー(try 節中、意図的に throw している部分)
      else if (error instanceof Error) {
        state.errors = {messages: error.message};
      }

      //不明
      else {
        state.errors = {messages: 'エラーが発生しました。しばらくしてから再度お試しください。'};
      }
    }
  };

  /**
   * ニックネームを追加する
   * @param nickname
   * @returns {Promise<*|string[]|number>}
   */
  const appendNickname = async (nickname) => {
    //送信 body の作成
    const body = new FormData();
    body.append('nickname', nickname);

    //投稿をサーバーへ依頼する
    try {
      //通信開始
      const response = await fetch(siteUrl+'/place/set-nickname', {
        method: 'post',
        headers: _getHeader(),
        body: body
      });

      //レスポンス異常発生
      if ( ! response.ok) {
        //もし 404 だったらログインしていない
        if (response.status === 404) {
          throw new Error('ログインセッションが切れている可能性があります。恐れ入りますがページをリロードして再度ログインしてください。');
        }

        //エラーメッセージが返っているか判定
        const parameter = await response.json();

        //もしエラーメッセージが存在すれば返す
        if ('errors' in parameter && Array.isArray(parameter.errors)) {
          throw parameter.errors;
        }
        if ('errors' in parameter && typeof parameter.errors === 'object' && parameter.errors !== null) {
          throw Object.keys(parameter.errors).map(key => parameter.errors[key].join("\n"));
        }
        if ('messages' in parameter && Array.isArray(parameter.messages)) {
          throw parameter.messages;
        }

        //もし単発エラーがあれば返す
        if ('message' in parameter && typeof parameter.message === 'string') {
          throw [parameter.message];
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //パラメータを得る
      const parameter = await response.json();

      //情報を更新する
      console.log(parameter);
      state.nicknames = parameter.nicknames;

      //成功を返す
      return Number(parameter.id);
    }

      //サーバーとの通信中、なんらかのエラーが発生
    catch (error) {
      //恐らく通信途絶
      if (error instanceof TypeError) {
        return ['ネットワーク接続に失敗しました。'];
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        return ['ネットワーク接続が中断されました。'];
      }

      //恐らくサーバーからのメッセージ
      else if (Array.isArray(error)) {
        return error;
      }

      //恐らくカスタムエラー
      else if (error instanceof Error) {
        return [error.message];
      }

      //不明
      else {
        return ['エラーが発生しました。しばらくしてから再度お試しください。'];
      }
    }
  };

  /**
   * 既存の投稿(多くは下書き)から投稿内容を復旧させる
   * @returns {Promise<void>}
   */
  const restore = async id => {
    try {
      //サーバーより情報を得る
      const response = await fetch(siteUrl+'/place/restore/'+id);

      //レスポンス異常発生
      if ( ! response.ok) {
        //もし 404 だったらログインしていない
        if (response.status === 404) {
          throw new Error('ログインセッションが切れている可能性があります。恐れ入りますがページをリロードして再度ログインしてください。');
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //パラメータを得る
      const parameter = await response.json();

      //情報を更新する
      state.emotion = parameter.emotion;
      state.categoryIds = parameter.categoryIds;
      state.nicknameId = parameter.nicknameId;
      state.title = parameter.title;
      state.content = parameter.content;
      state.acceptComments = parameter.acceptComments;
      state.draftId = Number(id);

      // localStorage に保存
      window.localStorage.setItem(localStorageKey, JSON.stringify({
        emotion: parameter.emotion,
        categoryIds: parameter.categoryIds,
        nicknameId: parameter.nicknameId,
        title: parameter.title,
        content: parameter.content,
        acceptComments: parameter.acceptComments,
      }));
    }

    catch (error) {
      //恐らく通信途絶
      if (error instanceof TypeError) {
        state.errors = {messages: 'ネットワーク接続に失敗しました。'};
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        state.errors = {messages: 'ネットワーク接続が中断されました。'};
      }

      //恐らくカスタムエラー(try 節中、意図的に throw している部分)
      else if (error instanceof Error) {
        state.errors = {messages: error.message};
      }

      //不明
      else {
        state.errors = {messages: 'エラーが発生しました。しばらくしてから再度お試しください。'};
      }
    }
  };

  /**
   * 下書きの ID をリセットする
   */
  const resetDraft = () => {
    state.draftId = 0;
  };

  /**
   * バリデーションを行い、問題があればエラーを更新する
   * @param name
   * @returns {Promise<void>}
   */
  const valid = async (name) => {
    //バリデーションをサーバーへ依頼する
    try {
      //通信開始
      const response = await fetch(siteUrl+'/place/valid/'+name, {
        method: 'post',
        headers: _getHeader(),
        body: _getFormData()
      });

      //レスポンス異常発生
      if ( ! response.ok) {
        //メッセージを JSON としてパース
        const json = await response.json();

        //もし errors が見つかったら例外を投げる
        if (
            typeof json.errors === 'object' &&
            json.errors !== null
        ) {
          throw json;
        }

        //もし message が見つかったら例外を投げる
        if (typeof json.message === 'string') {
          throw new Error(json.message);
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //問題なければ message と対象の項目をクリア
      state.errors.message = '';
      state.errors[name] = '';
    }

    //サーバーとの通信中、なんらかのエラーが発生
    catch (error) {
      //サーバーから得られた各バリデーション
      if (
          typeof error.errors === 'object' &&
          error.errors !== null
      ) {
        state.errors[name] = error.errors[name];
      }

      //恐らく通信途絶
      else if (error instanceof TypeError) {
        state.errors = {messages: 'ネットワーク接続に失敗しました。'};
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        state.errors = {messages: 'ネットワーク接続が中断されました。'};
      }

      //恐らくカスタムエラー(try 節中、意図的に throw している部分)
      else if (error instanceof Error) {
        state.errors = {messages: error.message};
      }

      //不明
      else {
        state.errors = {messages: 'エラーが発生しました。しばらくしてから再度お試しください。'};
      }
    }
  };

  /**
   * サーバーに投稿を依頼する
   * @returns {Promise<void>}
   */
  const post = async (isDfaft = false) => {
    //初回モーダルのポイント表記先(TOP ページ以外では存在しない)
    const pointTarget = document.getElementById('firstPoint');

    //投稿一覧(TOP ページ以外では存在しない)
    const list = document.getElementById('list');

    //投稿をサーバーへ依頼する
    try {
      //通信開始
      const response = await fetch(isDfaft ? siteUrl+'/place/draft' : siteUrl+'/place/post', {
        method: 'post',
        headers: _getHeader(),
        body: _getFormData()
      });

      //レスポンス異常発生
      if ( ! response.ok) {
        //メッセージを JSON としてパース
        const json = await response.json();

        //もし errors が見つかったら例外を投げる
        if (
          typeof json.errors === 'object' &&
          json.errors !== null
        ) {
          throw json;
        }

        //もし message が見つかったら例外を投げる
        if (typeof json.message === 'string') {
          throw json;
        }

        //エラーメッセージを文字列に変換して例外を投げる
        throw new Error('エラーが発生しました。しばらくしてから再度お試しください。');
      }

      //ローカルストレージのデータを削除
      window.localStorage.removeItem(localStorageKey);

      //問題なければ message と対象の項目をクリア
      state.errors = {};

      //入力内容のクリア
      state.emotion = 0;
      state.categoryIds = [];
      state.nicknameId = -2;
      state.title = '';
      state.content = '';
      state.isAnswersWanted = false;
      state.acceptComments = true;

      //応答を確認
      const results = await response.json();

      // クリアした内容で localStorage を更新
      _memoryLocalStorage();

      // 掲示板 TOP に遷移
      window.location.href = siteUrl+'/place';

      //成功を返す
      return true;
    }

      //サーバーとの通信中、なんらかのエラーが発生
    catch (error) {
      //恐らく通信途絶
      if (error instanceof TypeError) {
        state.errors = {messages: 'ネットワーク接続に失敗しました。'};
      }

      //恐らく意図的な中断
      else if (error instanceof DOMException) {
        state.errors = {messages: 'ネットワーク接続が中断されました。'};
      }

      //何かプログラムエラー
      else if (error instanceof Error) {
        console.log(error);
        state.errors = {messages: 'エラーが発生しました。しばらくしてから再度お試しください。'};
      }

      //恐らくカスタムエラー(try 節中、意図的に throw している部分)
      else if (error instanceof Object) {
        state.errors = error;
      }

      //不明
      else {
        state.errors = {messages: 'エラーが発生しました。しばらくしてから再度お試しください。'};
      }

      //失敗を返す
      return false;
    }
  };

  //提供
  return {
    state,
    nickname,
    errorLines,
    contentLength,
    isContentLength,
    isEditComplete,
    init,
    appendNickname,
    restore,
    resetDraft,
    valid,
    post
  };

};

//composition のエクスポート
export default usePost;

//composition を注入するためのキー
export const USE_POST_KEY = Symbol('usePost');
